// Single source of truth for analytics events + UTM attribution.
import { api } from "@/lib/api";
import { LEAD_TYPE } from "@/lib/constants";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const STORE_KEY = "gv_attribution";

// Parse UTM params once on load and persist them for the session.
export function captureAttribution() {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const existing = JSON.parse(sessionStorage.getItem(STORE_KEY) || "{}");
    let changed = false;
    UTM_KEYS.forEach((k) => {
        const v = params.get(k);
        if (v) {
            existing[k] = v;
            changed = true;
        }
    });
    if (changed) sessionStorage.setItem(STORE_KEY, JSON.stringify(existing));
}

function getAttribution() {
    if (typeof window === "undefined") return {};
    const utm = JSON.parse(sessionStorage.getItem(STORE_KEY) || "{}");
    return {
        source_page: window.location.pathname,
        source_url: window.location.href,
        ...utm,
    };
}

// Build the full lead payload merging form data with automatic attribution.
export function buildLeadPayload(formData, leadType, ctx = {}) {
    return {
        ...formData,
        lead_type: leadType,
        source_unit: ctx.unit || null,
        source_building: ctx.building || null,
        collection: ctx.collection || null,
        unit_surface: ctx.unit_surface ?? null,
        unit_balcony: ctx.unit_balcony ?? null,
        unit_living: ctx.unit_living ?? null,
        unit_floor: ctx.unit_floor || null,
        unit_status: ctx.unit_status || null,
        residence_type: ctx.residence_type || null,
        ...getAttribution(),
    };
}

// Fire a lead-less interaction event (whatsapp/phone/email clicks).
export async function trackClick(leadType, ctx = {}) {
    try {
        await api.post("/track", buildLeadPayload({}, leadType, ctx));
    } catch (err) {
        // Tracking must never block the user; log for diagnostics only.
        console.debug("trackClick failed (non-blocking):", err);
    }
}

export async function submitLead(formData, leadType, ctx = {}) {
    const { data } = await api.post("/leads", buildLeadPayload(formData, leadType, ctx));
    return data;
}

export async function submitAdminLeadPayload(payload) {
    const { data } = await api.post("/admin/leads", payload);
    return data;
}

// ---------- GTM/DataLayer (for Meta Lead via GTM) ----------
export const GENERATE_LEAD_EVENT = "generate_lead";

const GENERATE_LEAD_LABEL = {
    [LEAD_TYPE.GENERAL_CONTACT]: "Contact",
    [LEAD_TYPE.BOOK_SHOWROOM_VISIT]: "Contact",
    [LEAD_TYPE.CONTACT_ABOUT_UNIT]: "Contact",
    [LEAD_TYPE.MORTGAGE_INFO_REQUEST]: "Contact",
    [LEAD_TYPE.WHATSAPP_ENQUIRY]: "Contact",
    [LEAD_TYPE.DOWNLOAD_BROCHURE]: "Brochure",
};

export function pushDataLayerEvent(payload) {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
}

export function trackGenerateLead(eventLabel, extra = {}) {
    if (!eventLabel) return;
    pushDataLayerEvent({
        event: GENERATE_LEAD_EVENT,
        eventLabel,
        ...extra,
    });
}

export function trackGenerateLeadForLeadType(leadType, extra = {}) {
    const eventLabel = GENERATE_LEAD_LABEL[leadType];
    trackGenerateLead(eventLabel, extra);
}
